import { useNavigate } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useFamilyKYC } from "@/hooks/useFamilyKYC";
import {
  CheckCircle2,
  Circle,
  ClipboardList,
  Upload,
  FileCheck,
  Rocket,
  Loader2,
  ChevronRight,
  Mail,
  AlertCircle,
} from "lucide-react";

interface OnboardingStatusCardProps {
  familyId: string;
  orgId: string;
  familyName: string;
  onboardingStatus: string | null;
  portalEnabled?: boolean;
}

const STATUS_CONFIG: Record<string, { label: string; color: string; description: string }> = {
  draft: {
    label: "Draft",
    color: "bg-gray-500",
    description: "Family created, awaiting KYC",
  },
  kyc_pending: {
    label: "KYC Pending",
    color: "bg-yellow-500",
    description: "Waiting for KYC completion",
  },
  data_pending: {
    label: "Data Pending",
    color: "bg-blue-500",
    description: "KYC complete, awaiting portfolio data",
  },
  review: {
    label: "In Review",
    color: "bg-purple-500",
    description: "All data collected, awaiting approval",
  },
  active: {
    label: "Active",
    color: "bg-green-500",
    description: "Onboarding complete",
  },
};

const STEPS = [
  { key: "kyc", label: "KYC Information", icon: ClipboardList },
  { key: "portfolio", label: "Portfolio Data", icon: Upload },
  { key: "review", label: "Review", icon: FileCheck },
  { key: "active", label: "Activation", icon: Rocket },
];

export function OnboardingStatusCard({
  familyId,
  orgId,
  familyName,
  onboardingStatus,
  portalEnabled = false,
}: OnboardingStatusCardProps) {
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { completionStatus, completionPercentage, isLoading: kycLoading } = useFamilyKYC(familyId, orgId);

  // Determine current step
  const currentStepIndex = (() => {
    switch (onboardingStatus) {
      case "draft":
      case "kyc_pending":
        return 0;
      case "data_pending":
        return 1;
      case "review":
        return 2;
      case "active":
        return 3;
      default:
        return 0;
    }
  })();

  // Activate family mutation
  const activateMutation = useMutation({
    mutationFn: async () => {
      const { error } = await supabase
        .from("families")
        .update({
          onboarding_status: "active",
          activated_at: new Date().toISOString(),
        })
        .eq("id", familyId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["family-detail", familyId] });
      queryClient.invalidateQueries({ queryKey: ["families"] });
      toast({
        title: "Family Activated",
        description: `${familyName} has been activated successfully.`,
      });
    },
    onError: (error) => {
      toast({
        title: "Activation Failed",
        description: error instanceof Error ? error.message : "Failed to activate family",
        variant: "destructive",
      });
    },
  });

  // Request corrections mutation
  const requestCorrectionsMutation = useMutation({
    mutationFn: async () => {
      // Update status back to kyc_pending
      const { error } = await supabase
        .from("families")
        .update({ onboarding_status: "kyc_pending" })
        .eq("id", familyId);

      if (error) throw error;

      // TODO: Trigger notification email
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["family-detail", familyId] });
      queryClient.invalidateQueries({ queryKey: ["families"] });
      toast({
        title: "Corrections Requested",
        description: "The family has been notified to update their information.",
      });
    },
    onError: (error) => {
      toast({
        title: "Request Failed",
        description: error instanceof Error ? error.message : "Failed to request corrections",
        variant: "destructive",
      });
    },
  });

  const statusConfig = STATUS_CONFIG[onboardingStatus || "draft"] || STATUS_CONFIG.draft;

  // Don't show for active families
  if (onboardingStatus === "active") {
    return null;
  }

  return (
    <Card className="border-primary/20 bg-primary/5">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-lg flex items-center gap-2">
              <ClipboardList className="h-5 w-5" />
              Onboarding Status
            </CardTitle>
            <CardDescription>{statusConfig.description}</CardDescription>
          </div>
          <Badge className={`${statusConfig.color} text-white`}>
            {statusConfig.label}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Progress Steps */}
        <div className="flex items-center justify-between">
          {STEPS.map((step, idx) => {
            const Icon = step.icon;
            const isComplete = idx < currentStepIndex;
            const isCurrent = idx === currentStepIndex;
            const isPending = idx > currentStepIndex;

            return (
              <div key={step.key} className="flex items-center flex-1">
                <div className="flex flex-col items-center">
                  <div
                    className={`flex items-center justify-center h-10 w-10 rounded-full border-2 transition-colors ${
                      isComplete
                        ? "bg-primary border-primary text-primary-foreground"
                        : isCurrent
                        ? "border-primary text-primary bg-primary/10"
                        : "border-muted text-muted-foreground"
                    }`}
                  >
                    {isComplete ? (
                      <CheckCircle2 className="h-5 w-5" />
                    ) : (
                      <Icon className="h-5 w-5" />
                    )}
                  </div>
                  <span
                    className={`text-xs mt-2 text-center ${
                      isCurrent ? "font-medium text-primary" : "text-muted-foreground"
                    }`}
                  >
                    {step.label}
                  </span>
                </div>
                {idx < STEPS.length - 1 && (
                  <div
                    className={`flex-1 h-1 mx-2 rounded-full ${
                      isComplete ? "bg-primary" : "bg-muted"
                    }`}
                  />
                )}
              </div>
            );
          })}
        </div>

        {/* KYC Progress (when in KYC phase) */}
        {(onboardingStatus === "draft" || onboardingStatus === "kyc_pending") && !kycLoading && (
          <div className="space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span>KYC Completion</span>
              <span className="font-medium">{completionPercentage}%</span>
            </div>
            <Progress value={completionPercentage} className="h-2" />
            <div className="flex flex-wrap gap-2 mt-2">
              {[
                { key: "hasProfile", label: "Profile" },
                { key: "hasAddress", label: "Address" },
                { key: "hasOwners", label: "Owners" },
                { key: "hasEntities", label: "Entities" },
                { key: "hasBankAccounts", label: "Bank Accounts" },
              ].map((item) => {
                const isComplete = completionStatus[item.key as keyof typeof completionStatus];
                return (
                  <Badge
                    key={item.key}
                    variant={isComplete ? "default" : "outline"}
                    className="text-xs"
                  >
                    {isComplete ? (
                      <CheckCircle2 className="h-3 w-3 mr-1" />
                    ) : (
                      <Circle className="h-3 w-3 mr-1" />
                    )}
                    {item.label}
                  </Badge>
                );
              })}
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex flex-wrap gap-2 pt-2">
          {/* KYC Phase Actions */}
          {(onboardingStatus === "draft" || onboardingStatus === "kyc_pending") && (
            <>
              <Button
                onClick={() => navigate(`/onboarding/kyc/${familyId}`)}
                className="flex-1"
              >
                <ClipboardList className="h-4 w-4" />
                {completionPercentage > 0 ? "Continue KYC" : "Start KYC"}
                <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
              {portalEnabled && (
                <Button variant="outline" size="sm">
                  <Mail className="h-4 w-4" />
                  Send Reminder
                </Button>
              )}
            </>
          )}

          {/* Data Pending Phase Actions */}
          {onboardingStatus === "data_pending" && (
            <>
              <Button
                onClick={() => navigate(`/onboarding/import/${familyId}`)}
                className="flex-1"
              >
                <Upload className="h-4 w-4" />
                Import Portfolio Data
                <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate(`/onboarding/kyc/${familyId}`)}
              >
                <ClipboardList className="h-4 w-4" />
                Edit KYC
              </Button>
            </>
          )}

          {/* Review Phase Actions */}
          {onboardingStatus === "review" && (
            <>
              <Button
                onClick={() => activateMutation.mutate()}
                disabled={activateMutation.isPending}
                className="flex-1"
              >
                {activateMutation.isPending ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Activating...
                  </>
                ) : (
                  <>
                    <Rocket className="h-4 w-4" />
                    Approve & Activate
                  </>
                )}
              </Button>
              <Button
                variant="outline"
                onClick={() => requestCorrectionsMutation.mutate()}
                disabled={requestCorrectionsMutation.isPending}
              >
                {requestCorrectionsMutation.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <AlertCircle className="h-4 w-4" />
                )}
                Request Corrections
              </Button>
              <Button
                variant="ghost"
                onClick={() => navigate(`/onboarding/kyc/${familyId}`)}
              >
                View KYC
              </Button>
            </>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
